// Validated screening test definitions for Claira.
// All scoring matches the published clinical instruments.

export type ScreeningId =
  | "phq9" | "gad7" | "asrs" | "pcl5" | "ocir" | "aq10" | "mdq"
  | "epds" | "k10" | "audit" | "auditc" | "cage" | "dast10" | "scoff" | "wsas" | "who5";
export type Variant = "short" | "full";

export interface Question {
  id: string;
  text: string;
  options: { label: string; value: number }[];
  reverseScored?: boolean;
}

export interface ScreeningDef {
  id: ScreeningId;
  title: string;
  fullName: string;
  description: string;
  duration: string;
  category:
    | "Depression" | "Anxiety" | "ADHD" | "PTSD" | "OCD" | "Autism" | "Bipolar"
    | "Perinatal Depression" | "Psychological Distress" | "Alcohol Use"
    | "Drug Use" | "Eating Disorders" | "Functional Impairment" | "Wellbeing";
  variant: Variant;
  questions: Question[];
  /** Returns severity bands & whether result is positive (warrants follow-up). */
  interpret: (score: number) => { severity: string; positive: boolean; band: string; clinicianNote: string };
  hasExtended?: ScreeningId; // longer follow-up if positive
  isExtensionOf?: ScreeningId;
}

const yesNo = [
  { label: "No", value: 0 },
  { label: "Yes", value: 1 },
];

const freq4 = [
  { label: "Not at all", value: 0 },
  { label: "Several days", value: 1 },
  { label: "More than half the days", value: 2 },
  { label: "Nearly every day", value: 3 },
];

const asrsFreq = [
  { label: "Never", value: 0 },
  { label: "Rarely", value: 1 },
  { label: "Sometimes", value: 2 },
  { label: "Often", value: 3 },
  { label: "Very Often", value: 4 },
];

const pclFreq = [
  { label: "Not at all", value: 0 },
  { label: "A little bit", value: 1 },
  { label: "Moderately", value: 2 },
  { label: "Quite a bit", value: 3 },
  { label: "Extremely", value: 4 },
];

const ociFreq = [
  { label: "Not at all", value: 0 },
  { label: "A little", value: 1 },
  { label: "Moderately", value: 2 },
  { label: "A lot", value: 3 },
  { label: "Extremely", value: 4 },
];

const aqAgree = [
  { label: "Definitely Agree", value: 1 },
  { label: "Slightly Agree", value: 1 },
  { label: "Slightly Disagree", value: 0 },
  { label: "Definitely Disagree", value: 0 },
];
const aqAgreeReverse = [
  { label: "Definitely Agree", value: 0 },
  { label: "Slightly Agree", value: 0 },
  { label: "Slightly Disagree", value: 1 },
  { label: "Definitely Disagree", value: 1 },
];

// RAADS-R response scale (0–3). Normative (reverse-scored) items invert the scale.
const raadsScale = [
  { label: "True now and when I was young", value: 3 },
  { label: "True only now", value: 2 },
  { label: "True only when I was younger than 16", value: 1 },
  { label: "Never true", value: 0 },
];
const raadsScaleReverse = [
  { label: "True now and when I was young", value: 0 },
  { label: "True only now", value: 1 },
  { label: "True only when I was younger than 16", value: 2 },
  { label: "Never true", value: 3 },
];

// ---------------- PHQ-9 (Depression) ----------------
export const phq9: ScreeningDef = {
  id: "phq9",
  title: "PHQ-9",
  fullName: "Patient Health Questionnaire-9 (Depression)",
  description: "9-item depression screening. Over the last 2 weeks, how often have you been bothered by the following?",
  duration: "~3 min",
  category: "Depression",
  variant: "full",
  questions: [
    "Little interest or pleasure in doing things",
    "Feeling down, depressed, or hopeless",
    "Trouble falling/staying asleep, or sleeping too much",
    "Feeling tired or having little energy",
    "Poor appetite or overeating",
    "Feeling bad about yourself — or that you are a failure",
    "Trouble concentrating on things",
    "Moving or speaking so slowly that others noticed — or being fidgety/restless",
    "Thoughts that you would be better off dead, or of hurting yourself",
  ].map((t, i) => ({ id: `q${i + 1}`, text: t, options: freq4 })),
  interpret: (s) => {
    if (s <= 4) return { severity: "Minimal", band: "0–4", positive: false, clinicianNote: "Minimal depressive symptoms." };
    if (s <= 9) return { severity: "Mild", band: "5–9", positive: false, clinicianNote: "Mild depressive symptoms; watchful waiting recommended." };
    if (s <= 14) return { severity: "Moderate", band: "10–14", positive: true, clinicianNote: "Moderate depression; consider counselling and clinical follow-up." };
    if (s <= 19) return { severity: "Moderately Severe", band: "15–19", positive: true, clinicianNote: "Moderately severe depression; active treatment with pharmacotherapy and/or psychotherapy is indicated." };
    return { severity: "Severe", band: "20–27", positive: true, clinicianNote: "Severe depression; immediate initiation of treatment is warranted." };
  },
};

// ---------------- GAD-7 (Anxiety) ----------------
export const gad7: ScreeningDef = {
  id: "gad7",
  title: "GAD-7",
  fullName: "Generalized Anxiety Disorder-7",
  description: "7-item anxiety screening. Over the last 2 weeks, how often have you been bothered by the following?",
  duration: "~2 min",
  category: "Anxiety",
  variant: "full",
  questions: [
    "Feeling nervous, anxious, or on edge",
    "Not being able to stop or control worrying",
    "Worrying too much about different things",
    "Trouble relaxing",
    "Being so restless that it's hard to sit still",
    "Becoming easily annoyed or irritable",
    "Feeling afraid as if something awful might happen",
  ].map((t, i) => ({ id: `q${i + 1}`, text: t, options: freq4 })),
  interpret: (s) => {
    if (s <= 4) return { severity: "Minimal", band: "0–4", positive: false, clinicianNote: "Minimal anxiety." };
    if (s <= 9) return { severity: "Mild", band: "5–9", positive: false, clinicianNote: "Mild anxiety; monitor." };
    if (s <= 14) return { severity: "Moderate", band: "10–14", positive: true, clinicianNote: "Moderate anxiety; further evaluation recommended." };
    return { severity: "Severe", band: "15–21", positive: true, clinicianNote: "Severe anxiety; active treatment is likely warranted." };
  },
};

// ---------------- ASRS (ADHD) — short Part A (6 items) ----------------
// Threshold check uses item-specific cutoffs; we score 1 if the response meets the shaded box.
const asrsShortItems = [
  { id: "q1", text: "How often do you have trouble wrapping up the final details of a project, once the challenging parts are done?", threshold: 2 }, // Sometimes+
  { id: "q2", text: "How often do you have difficulty getting things in order when you have to do a task that requires organization?", threshold: 2 },
  { id: "q3", text: "How often do you have problems remembering appointments or obligations?", threshold: 2 },
  { id: "q4", text: "When you have a task that requires a lot of thought, how often do you avoid or delay getting started?", threshold: 3 }, // Often+
  { id: "q5", text: "How often do you fidget or squirm with your hands or feet when you have to sit down for a long time?", threshold: 3 },
  { id: "q6", text: "How often do you feel overly active and compelled to do things, like you were driven by a motor?", threshold: 3 },
];

export const asrsShort: ScreeningDef = {
  id: "asrs",
  title: "ASRS-v1.1 (Part A)",
  fullName: "Adult ADHD Self-Report Scale — Screener",
  description: "6-item ADHD screener. Please answer how often each statement applied to you over the past 6 months.",
  duration: "~3 min",
  category: "ADHD",
  variant: "short",
  hasExtended: "asrs",
  questions: asrsShortItems.map((q) => ({ id: q.id, text: q.text, options: asrsFreq })),
  interpret: (s) => {
    // s here is count of items meeting threshold (computed at submission)
    if (s >= 4) return { severity: "Highly indicative", band: `${s}/6 flagged`, positive: true, clinicianNote: "Symptoms highly consistent with adult ADHD; full clinical evaluation recommended." };
    return { severity: "Below screening threshold", band: `${s}/6 flagged`, positive: false, clinicianNote: "Below ADHD screening threshold." };
  },
};

// Full ASRS Part B (12 additional items)
const asrsFullItems = [
  ...asrsShortItems.map((q) => ({ id: q.id, text: q.text })),
  { id: "q7", text: "How often do you make careless mistakes when working on a boring or difficult project?" },
  { id: "q8", text: "How often do you have difficulty keeping your attention when doing boring or repetitive work?" },
  { id: "q9", text: "How often do you have difficulty concentrating on what people say even when speaking directly to you?" },
  { id: "q10", text: "How often do you misplace or have difficulty finding things at home or at work?" },
  { id: "q11", text: "How often are you distracted by activity or noise around you?" },
  { id: "q12", text: "How often do you leave your seat in meetings or other situations where you are expected to remain seated?" },
  { id: "q13", text: "How often do you feel restless or fidgety?" },
  { id: "q14", text: "How often do you have difficulty unwinding and relaxing when you have time to yourself?" },
  { id: "q15", text: "How often do you find yourself talking too much when you are in social situations?" },
  { id: "q16", text: "When you're in a conversation, how often do you finish the sentences of the people you are talking to?" },
  { id: "q17", text: "How often do you have difficulty waiting your turn in situations when turn taking is required?" },
  { id: "q18", text: "How often do you interrupt others when they are busy?" },
];
export const asrsFull: ScreeningDef = {
  ...asrsShort,
  variant: "full",
  title: "ASRS-v1.1 (Full)",
  duration: "~7 min",
  hasExtended: undefined,
  isExtensionOf: "asrs",
  questions: asrsFullItems.map((q) => ({ id: q.id, text: q.text, options: asrsFreq })),
  interpret: (s) => {
    if (s >= 24) return { severity: "Highly indicative", band: `${s}/72`, positive: true, clinicianNote: "Symptom burden highly consistent with adult ADHD; clinical evaluation strongly recommended." };
    if (s >= 16) return { severity: "Possible ADHD", band: `${s}/72`, positive: true, clinicianNote: "Possible ADHD; further evaluation recommended." };
    return { severity: "Below threshold", band: `${s}/72`, positive: false, clinicianNote: "Below typical ADHD threshold." };
  },
};

// ---------------- PCL-5 short (PC-PTSD-5) ----------------
export const pcl5Short: ScreeningDef = {
  id: "pcl5",
  title: "PC-PTSD-5",
  fullName: "Primary Care PTSD Screen for DSM-5",
  description: "5-item PTSD screen. In the past month, have you...",
  duration: "~2 min",
  category: "PTSD",
  variant: "short",
  hasExtended: "pcl5",
  questions: [
    "Had nightmares about a traumatic event(s) or thought about it when you did not want to?",
    "Tried hard not to think about it or went out of your way to avoid situations that reminded you of it?",
    "Been constantly on guard, watchful, or easily startled?",
    "Felt numb or detached from people, activities, or your surroundings?",
    "Felt guilty or unable to stop blaming yourself or others for the event(s) or any problems it may have caused?",
  ].map((t, i) => ({ id: `q${i + 1}`, text: t, options: yesNo })),
  interpret: (s) => {
    if (s >= 3) return { severity: "Probable PTSD", band: `${s}/5`, positive: true, clinicianNote: "Score ≥3 — probable PTSD; full PCL-5 assessment recommended." };
    return { severity: "Below screening threshold", band: `${s}/5`, positive: false, clinicianNote: "Below PTSD screening threshold." };
  },
};

export const pcl5Full: ScreeningDef = {
  id: "pcl5",
  title: "PCL-5",
  fullName: "PTSD Checklist for DSM-5",
  description: "20-item PTSD self-report. In the past month, how much were you bothered by:",
  duration: "~8 min",
  category: "PTSD",
  variant: "full",
  isExtensionOf: "pcl5",
  questions: [
    "Repeated, disturbing, and unwanted memories of the stressful experience",
    "Repeated, disturbing dreams of the stressful experience",
    "Suddenly feeling or acting as if the stressful experience were happening again",
    "Feeling very upset when something reminded you of the stressful experience",
    "Having strong physical reactions when something reminded you of the experience",
    "Avoiding memories, thoughts, or feelings related to the stressful experience",
    "Avoiding external reminders of the experience (people, places, conversations, activities)",
    "Trouble remembering important parts of the stressful experience",
    "Having strong negative beliefs about yourself, others, or the world",
    "Blaming yourself or someone else for the stressful experience or what happened after it",
    "Having strong negative feelings such as fear, horror, anger, guilt, or shame",
    "Loss of interest in activities you used to enjoy",
    "Feeling distant or cut off from other people",
    "Trouble experiencing positive feelings",
    "Irritable behavior, angry outbursts, or acting aggressively",
    "Taking too many risks or doing things that could cause you harm",
    "Being 'super-alert' or watchful or on guard",
    "Feeling jumpy or easily startled",
    "Having difficulty concentrating",
    "Trouble falling or staying asleep",
  ].map((t, i) => ({ id: `q${i + 1}`, text: t, options: pclFreq })),
  interpret: (s) => {
    if (s >= 33) return { severity: "Probable PTSD", band: `${s}/80`, positive: true, clinicianNote: "Score ≥33 suggests probable PTSD diagnosis; clinical evaluation recommended." };
    if (s >= 20) return { severity: "Sub-threshold symptoms", band: `${s}/80`, positive: true, clinicianNote: "Sub-threshold PTSD symptoms; consider further assessment." };
    return { severity: "Below threshold", band: `${s}/80`, positive: false, clinicianNote: "Below PTSD screening threshold." };
  },
};

// ---------------- OCD: OCI-R (18 items, short single form) ----------------
export const ocir: ScreeningDef = {
  id: "ocir",
  title: "OCI-R",
  fullName: "Obsessive-Compulsive Inventory — Revised",
  description: "18-item OCD screening. The following statements refer to experiences that many people have. Rate how much each has distressed or bothered you during the past month.",
  duration: "~5 min",
  category: "OCD",
  variant: "full",
  questions: [
    "I have saved up so many things that they get in the way",
    "I check things more often than necessary",
    "I get upset if objects are not arranged properly",
    "I feel compelled to count while I am doing things",
    "I find it difficult to touch an object when I know it has been touched by strangers or certain people",
    "I find it difficult to control my own thoughts",
    "I collect things I don't need",
    "I repeatedly check doors, windows, drawers, etc.",
    "I get upset if others change the way I have arranged things",
    "I feel I have to repeat certain numbers",
    "I sometimes have to wash or clean myself simply because I feel contaminated",
    "I am upset by unpleasant thoughts that come into my mind against my will",
    "I avoid throwing things away because I am afraid I might need them later",
    "I repeatedly check gas and water taps and light switches after turning them off",
    "I need things to be arranged in a particular order",
    "I feel that there are good and bad numbers",
    "I wash my hands more often and longer than necessary",
    "I frequently get nasty thoughts and have difficulty in getting rid of them",
  ].map((t, i) => ({ id: `q${i + 1}`, text: t, options: ociFreq })),
  interpret: (s) => {
    if (s >= 21) return { severity: "Probable OCD", band: `${s}/72`, positive: true, clinicianNote: "Score ≥21 — likely OCD; clinical assessment recommended." };
    if (s >= 14) return { severity: "Sub-threshold OCD symptoms", band: `${s}/72`, positive: true, clinicianNote: "Elevated OC symptoms; consider clinical evaluation." };
    return { severity: "Below threshold", band: `${s}/72`, positive: false, clinicianNote: "Below OCD screening threshold." };
  },
};

// ---------------- AQ-10 (Autism) ----------------
const aqReverseIdx = new Set([0, 2, 4, 5]); // items 1,3,5,6 reverse-scored (per AQ-10 v1)
export const aq10: ScreeningDef = {
  id: "aq10",
  title: "AQ-10",
  fullName: "Autism-Spectrum Quotient (10-item)",
  description: "Brief autism screening for adults. Please indicate how much you agree or disagree.",
  duration: "~3 min",
  category: "Autism",
  variant: "full",
  questions: [
    "I often notice small sounds when others do not",
    "I usually concentrate more on the whole picture, rather than the small details",
    "I find it easy to do more than one thing at once",
    "If there is an interruption, I can switch back to what I was doing very quickly",
    "I find it easy to 'read between the lines' when someone is talking to me",
    "I know how to tell if someone listening to me is getting bored",
    "When I'm reading a story I find it difficult to work out the characters' intentions",
    "I like to collect information about categories of things (e.g. types of car, bird, train, plant)",
    "I find it easy to work out what someone is thinking or feeling just by looking at their face",
    "I find it difficult to work out people's intentions",
  ].map((t, i) => ({
    id: `q${i + 1}`,
    text: t,
    options: aqReverseIdx.has(i) ? aqAgreeReverse : aqAgree,
    reverseScored: aqReverseIdx.has(i),
  })),
  interpret: (s) => {
    if (s >= 6) return { severity: "Above threshold", band: `${s}/10`, positive: true, clinicianNote: "Score ≥6 — consider referral for full diagnostic assessment for autism spectrum condition." };
    return { severity: "Below threshold", band: `${s}/10`, positive: false, clinicianNote: "Below AQ-10 screening threshold." };
  },
};

// ---------------- RAADS-R (Ritvo Autism Asperger Diagnostic Scale-Revised) ----------------
// 80 items. Each item scored 0–3 (max 240). Cutoff ≥65 indicates likely ASD.
// 17 "normative" items are reverse-scored (true 1-based item numbers per Ritvo et al., 2011):
// 7, 17, 20, 24, 31, 35, 38, 39, 43, 51, 53, 60, 67, 69, 72, 77, 79.
const raadsItems: string[] = [
  "I am a sympathetic person.",
  "I often use words and phrases from movies and television in conversations.",
  "I am often surprised when others tell me I have been rude.",
  "Sometimes I talk too loudly or too softly, and I am not aware of it.",
  "I often don't know how to act in social situations.",
  "I can chat and make small talk with people.",
  "When I make conversation it is just to be polite. (R)",
  "Loud noises such as vacuum cleaners, electric drills can really bother me.",
  "It is difficult for me to understand how other people are feeling when we are talking.",
  "I like to be by myself as much as I can.",
  "I tend to use different tones of voice when I am with different people (e.g. children, dogs, etc.).",
  "It is difficult to figure out what other people expect of me.",
  "I like having a conversation with several people, for instance around a dinner table, at school or at work.",
  "I get along with other people by following a set of specific rules that help me look normal.",
  "It is very difficult for me to work and function in groups.",
  "When I am talking to someone, it is hard to change the subject. If the other person does so, I can get upset and confused.",
  "Sometimes I have to cover my ears to block out painful noises (like vacuum cleaners or people talking too much or too loudly). (R)",
  "It can be very hard to read someone's face, hand, and body movements when we are talking.",
  "I focus on details rather than the overall idea.",
  "I take things too literally, so I often miss what people are trying to say. (R)",
  "It is very difficult for me to understand when someone is embarrassed or jealous.",
  "Some ordinary textures that do not bother others feel very offensive when they touch my skin.",
  "I get extremely upset when the way I like to do things is suddenly changed.",
  "I have never wanted or needed to have what other people call an 'intimate relationship'. (R)",
  "It is difficult for me to start and stop a conversation. I need to keep going until I am finished.",
  "I speak with a normal rhythm.",
  "The same sound sometimes seems very loud or very soft, even though I know it has not changed.",
  "I enjoy spending time eating and talking with my family and friends.",
  "I can't tolerate things I dislike (like smells, textures, sounds or colors).",
  "I don't like to be hugged or held.",
  "When I go somewhere, I have to follow a familiar route or I can get very confused and upset. (R)",
  "It is difficult to figure out what other people expect of me.",
  "I like to talk things over with my friends.",
  "I cannot tell when someone is flirting with me.",
  "I have a very good memory, especially for numbers, dates, or details. (R)",
  "I like to copy the way certain people speak and act. It helps me appear more normal.",
  "It only takes me a few minutes to get to know someone.",
  "I keep lists of things that interest me, even when they have no practical use (for example sports statistics, train schedules, calendars, etc.). (R)",
  "I get very tired after socializing, even if I enjoyed myself. (R)",
  "Many simple tasks (e.g. getting up in the morning, having a bath/shower, getting dressed) are very difficult for me because I have problems organizing them in time.",
  "When talking to someone, I have a hard time telling when it is my turn to talk or to listen.",
  "I am considered a compassionate type of person.",
  "I get along with other people by following a set of specific rules that help me look normal. (R)",
  "It is difficult for me to understand how other people are feeling when we are talking.",
  "I am an understanding type of person.",
  "I do not connect with characters in movies and cannot feel what they feel.",
  "I cannot tell when someone is interested or bored with what I am saying.",
  "It can be very hard to read someone's face, hand, and body movements when we are talking.",
  "The same sound sometimes seems very loud or very soft, even though I know it has not changed.",
  "I enjoy spending time eating and talking with my family and friends.",
  "I can chat and make small talk with people. (R)",
  "Sometimes I tap or shake my hands to calm myself down.",
  "Sometimes I rock or shake my body to calm myself down. (R)",
  "I have to 'act normal' to please other people and make them like me.",
  "Meeting new people is usually easy for me.",
  "I get highly confused when someone interrupts me when I am talking about something I am very interested in.",
  "It is easy for me to make friends.",
  "I am bad at social chit-chat.",
  "People often say I gave responses that are 'off topic' or do not make sense in a conversation.",
  "I tend to talk and act in a monotone way, with little emotion. (R)",
  "I think I am very good at deal-making or negotiating.",
  "I would rather go out to eat in a restaurant by myself than with someone I know.",
  "I am considered a loner by those who know me best.",
  "I usually speak in a normal tone.",
  "I like things to be exactly the same day after day and even small changes in my routines upset me.",
  "How to make friends and socialize is a mystery to me.",
  "It calms me to be touching or holding something when I am anxious.",
  "When with other people, I generally separate myself from my own feelings and just shut down. (R)",
  "I find it very hard to understand jokes, irony, or sarcasm.",
  "I always notice how food feels in my mouth. This is more important to me than how it tastes. (R)",
  "I have at times had to pretend to be 'normal' to get through some situations.",
  "I think or talk about the same things over and over, and I cannot help it. (R)",
  "When I feel overwhelmed by my senses, I have to isolate myself to shut them down.",
  "I like to talk things over with my friends.",
  "I cannot tell what someone else is feeling about a particular topic during a discussion.",
  "It is hard for me to make new friends because I cannot start a conversation with strangers.",
  "I feel very comfortable with dating or being in social situations with others. (R)",
  "I try to be as helpful as I can when other people tell me their personal problems.",
  "I have been told I am rude even though I think I am being polite. (R)",
  "It is very difficult for me to understand when someone is embarrassed or jealous. (R)",
  "I sometimes lose touch with reality.",
];
// Reverse-scored item indices (0-based) per Ritvo et al. 2011 normative set:
const raadsReverseIdx = new Set([6, 16, 19, 23, 30, 34, 37, 38, 42, 50, 52, 59, 66, 68, 71, 76, 78]);

export const raadsR: ScreeningDef = {
  id: "aq10",
  title: "RAADS-R",
  fullName: "Ritvo Autism Asperger Diagnostic Scale — Revised",
  description:
    "80-item in-depth autism self-report. For each statement, choose the answer that best describes you across your lifetime.",
  duration: "~25 min",
  category: "Autism",
  variant: "full",
  isExtensionOf: "aq10",
  questions: raadsItems.map((t, i) => ({
    id: `q${i + 1}`,
    text: t.replace(/\s*\(R\)\s*$/, ""),
    options: raadsReverseIdx.has(i) ? raadsScaleReverse : raadsScale,
    reverseScored: raadsReverseIdx.has(i),
  })),
  interpret: (s) => {
    if (s >= 130) return { severity: "Strongly indicative", band: `${s}/240`, positive: true, clinicianNote: "Score ≥130 strongly suggestive of ASD; full diagnostic assessment by a specialist clinician is recommended." };
    if (s >= 65) return { severity: "Above ASD threshold", band: `${s}/240`, positive: true, clinicianNote: "Score ≥65 — above the RAADS-R threshold for likely ASD; clinical assessment recommended." };
    return { severity: "Below threshold", band: `${s}/240`, positive: false, clinicianNote: "Below the RAADS-R threshold for likely ASD." };
  },
};

// AQ-10 acts as a short screener that opens RAADS-R if positive.
aq10.variant = "short";
aq10.hasExtended = "aq10";

// ---------------- Catalog ----------------
export const SCREENINGS: Record<string, ScreeningDef> = {
  phq9, gad7,
  asrs: asrsShort, asrs_full: asrsFull,
  pcl5: pcl5Short, pcl5_full: pcl5Full,
  ocir,
  aq10, aq10_full: raadsR,
};

// Top-level "first-pass" catalog for the screenings landing page
// (mdq appended at file end, after definition)
export const PRIMARY_SCREENINGS: ScreeningDef[] = [phq9, gad7, asrsShort, pcl5Short, ocir, aq10];

export function scoreScreening(def: ScreeningDef, responses: Record<string, number>): number {
  // ASRS short uses threshold counting
  if (def.id === "asrs" && def.variant === "short") {
    let count = 0;
    asrsShortItems.forEach((q) => {
      const v = responses[q.id];
      if (typeof v === "number" && v >= q.threshold) count++;
    });
    return count;
  }
  // MDQ uses a composite encoded score so interpret() can recover symptom_count, cluster and severity.
  if (def.id === "mdq") {
    let symptomCount = 0;
    for (let i = 1; i <= 13; i++) symptomCount += responses[`q${i}`] === 1 ? 1 : 0;
    const cluster = responses["q14"] === 1 ? 1 : 0;
    const severity = Math.max(0, Math.min(3, responses["q15"] ?? 0));
    return symptomCount * 100 + cluster * 10 + severity;
  }
  return def.questions.reduce((sum, q) => sum + (responses[q.id] ?? 0), 0);
}

export function getDef(id: ScreeningId, variant: Variant = "short"): ScreeningDef {
  if (id === "asrs") return variant === "full" ? asrsFull : asrsShort;
  if (id === "pcl5") return variant === "full" ? pcl5Full : pcl5Short;
  if (id === "aq10") return variant === "full" ? raadsR : aq10;
  return SCREENINGS[id];
}

/** Resource recommendations keyed to screening + outcome severity. */
export function recommendedResources(id: ScreeningId, positive: boolean): string[] {
  const base: string[] = ["self-care", "coping-strategies", "educational"];
  if (positive) base.push("professional-help", "therapy-tools");
  if (id === "phq9" || id === "pcl5") base.push("crisis-support");
  return Array.from(new Set(base));
}

// ---------------- MDQ (Mood Disorder Questionnaire — bipolar screen) ----------------
// 13 yes/no symptom items (q1..q13) + q14 (clustering yes/no) + q15 (problem severity 0..3).
// Positive screen: ≥7 of q1..q13 = yes, AND q14 = yes, AND q15 ≥ 2 (moderate or serious).
const mdqProblem = [
  { label: "No problem", value: 0 },
  { label: "Minor problem", value: 1 },
  { label: "Moderate problem", value: 2 },
  { label: "Serious problem", value: 3 },
];

const mdqSymptomItems = [
  "you felt so good or so hyper that other people thought you were not your normal self, or you were so hyper that you got into trouble?",
  "you were so irritable that you shouted at people or started fights or arguments?",
  "you felt much more self-confident than usual?",
  "you got much less sleep than usual and found you didn't really miss it?",
  "you were much more talkative or spoke faster than usual?",
  "thoughts raced through your head or you couldn't slow your mind down?",
  "you were so easily distracted by things around you that you had trouble concentrating or staying on track?",
  "you had much more energy than usual?",
  "you were much more active or did many more things than usual?",
  "you were much more social or outgoing than usual — for example, you telephoned friends in the middle of the night?",
  "you were much more interested in sex than usual?",
  "you did things that were unusual for you, or that other people might have thought were excessive, foolish, or risky?",
  "spending money got you or your family into trouble?",
];

export const mdq: ScreeningDef = {
  id: "mdq",
  title: "MDQ",
  fullName: "Mood Disorder Questionnaire (Bipolar Spectrum Screen)",
  description:
    "Has there ever been a period of time when you were not your usual self and… (please answer each item)",
  duration: "~4 min",
  category: "Bipolar",
  variant: "full",
  questions: [
    ...mdqSymptomItems.map((t, i) => ({ id: `q${i + 1}`, text: t, options: yesNo })),
    {
      id: "q14",
      text: "If you checked YES to more than one of the above, have several of these ever happened during the same period of time?",
      options: yesNo,
    },
    {
      id: "q15",
      text: "How much of a problem did any of these cause you — like being unable to work; having family, money, or legal troubles; or getting into arguments or fights?",
      options: mdqProblem,
    },
  ],
  interpret: (s) => {
    // s is encoded as: symptom_count * 10 + (cluster ? 1 : 0), severity reflected in band string.
    // We use a custom scorer; here we just decode to severity bands.
    const symptomCount = Math.floor(s / 100);
    const cluster = Math.floor((s % 100) / 10) === 1;
    const severity = s % 10;
    const positive = symptomCount >= 7 && cluster && severity >= 2;
    if (positive) {
      return {
        severity: "Positive screen",
        band: `${symptomCount}/13 symptoms · clustered · ${["no","minor","moderate","serious"][severity]} impact`,
        positive: true,
        clinicianNote:
          "Positive MDQ screen — ≥7 manic/hypomanic symptoms, co-occurring, with at least moderate functional impact. A full clinical assessment for bipolar spectrum disorder is recommended.",
      };
    }
    return {
      severity: "Below screening threshold",
      band: `${symptomCount}/13 symptoms${cluster ? " · clustered" : ""} · ${["no","minor","moderate","serious"][severity]} impact`,
      positive: false,
      clinicianNote: "Does not meet the MDQ threshold for a positive bipolar spectrum screen.",
    };
  },
};

SCREENINGS.mdq = mdq;
PRIMARY_SCREENINGS.push(mdq);

// =====================================================================
// Additional public-domain / free-clinical-use screenings
// =====================================================================

// ---------------- EPDS (Edinburgh Postnatal Depression Scale) ----------------
// 10 items, mixed scoring (some "reverse"). Items 3, 5–10 high-score = bad.
// Max 30. ≥10 possible depression, ≥13 likely depression. Item 10 = self-harm.
const epdsItems: { text: string; opts: { label: string; value: number }[] }[] = [
  { text: "I have been able to laugh and see the funny side of things", opts: [
    { label: "As much as I always could", value: 0 },
    { label: "Not quite so much now", value: 1 },
    { label: "Definitely not so much now", value: 2 },
    { label: "Not at all", value: 3 },
  ]},
  { text: "I have looked forward with enjoyment to things", opts: [
    { label: "As much as I ever did", value: 0 },
    { label: "Rather less than I used to", value: 1 },
    { label: "Definitely less than I used to", value: 2 },
    { label: "Hardly at all", value: 3 },
  ]},
  { text: "I have blamed myself unnecessarily when things went wrong", opts: [
    { label: "Yes, most of the time", value: 3 },
    { label: "Yes, some of the time", value: 2 },
    { label: "Not very often", value: 1 },
    { label: "No, never", value: 0 },
  ]},
  { text: "I have been anxious or worried for no good reason", opts: [
    { label: "No, not at all", value: 0 },
    { label: "Hardly ever", value: 1 },
    { label: "Yes, sometimes", value: 2 },
    { label: "Yes, very often", value: 3 },
  ]},
  { text: "I have felt scared or panicky for no very good reason", opts: [
    { label: "Yes, quite a lot", value: 3 },
    { label: "Yes, sometimes", value: 2 },
    { label: "No, not much", value: 1 },
    { label: "No, not at all", value: 0 },
  ]},
  { text: "Things have been getting on top of me", opts: [
    { label: "Yes, most of the time I haven't been able to cope at all", value: 3 },
    { label: "Yes, sometimes I haven't been coping as well as usual", value: 2 },
    { label: "No, most of the time I have coped quite well", value: 1 },
    { label: "No, I have been coping as well as ever", value: 0 },
  ]},
  { text: "I have been so unhappy that I have had difficulty sleeping", opts: [
    { label: "Yes, most of the time", value: 3 },
    { label: "Yes, sometimes", value: 2 },
    { label: "Not very often", value: 1 },
    { label: "No, not at all", value: 0 },
  ]},
  { text: "I have felt sad or miserable", opts: [
    { label: "Yes, most of the time", value: 3 },
    { label: "Yes, quite often", value: 2 },
    { label: "Not very often", value: 1 },
    { label: "No, not at all", value: 0 },
  ]},
  { text: "I have been so unhappy that I have been crying", opts: [
    { label: "Yes, most of the time", value: 3 },
    { label: "Yes, quite often", value: 2 },
    { label: "Only occasionally", value: 1 },
    { label: "No, never", value: 0 },
  ]},
  { text: "The thought of harming myself has occurred to me", opts: [
    { label: "Yes, quite often", value: 3 },
    { label: "Sometimes", value: 2 },
    { label: "Hardly ever", value: 1 },
    { label: "Never", value: 0 },
  ]},
];
export const epds: ScreeningDef = {
  id: "epds",
  title: "EPDS",
  fullName: "Edinburgh Postnatal Depression Scale",
  description: "10-item screen for depression during pregnancy and after birth. In the past 7 days…",
  duration: "~3 min",
  category: "Perinatal Depression",
  variant: "full",
  questions: epdsItems.map((q, i) => ({ id: `q${i + 1}`, text: q.text, options: q.opts })),
  interpret: (s) => {
    if (s >= 13) return { severity: "Likely depression", band: `${s}/30`, positive: true, clinicianNote: "Score ≥13 — likely perinatal depression; refer to GP, midwife or health visitor for assessment." };
    if (s >= 10) return { severity: "Possible depression", band: `${s}/30`, positive: true, clinicianNote: "Score ≥10 — possible perinatal depression; further assessment recommended." };
    return { severity: "Below threshold", band: `${s}/30`, positive: false, clinicianNote: "Below the EPDS screening threshold." };
  },
};

// ---------------- K10 (Kessler Psychological Distress) ----------------
const k10Freq = [
  { label: "None of the time", value: 1 },
  { label: "A little of the time", value: 2 },
  { label: "Some of the time", value: 3 },
  { label: "Most of the time", value: 4 },
  { label: "All of the time", value: 5 },
];
export const k10: ScreeningDef = {
  id: "k10",
  title: "K10",
  fullName: "Kessler Psychological Distress Scale",
  description: "10-item screen for non-specific psychological distress. In the past 30 days, about how often did you feel…",
  duration: "~3 min",
  category: "Psychological Distress",
  variant: "full",
  questions: [
    "tired out for no good reason?",
    "nervous?",
    "so nervous that nothing could calm you down?",
    "hopeless?",
    "restless or fidgety?",
    "so restless you could not sit still?",
    "depressed?",
    "that everything was an effort?",
    "so sad that nothing could cheer you up?",
    "worthless?",
  ].map((t, i) => ({ id: `q${i + 1}`, text: t, options: k10Freq })),
  interpret: (s) => {
    if (s >= 30) return { severity: "Severe distress", band: `${s}/50`, positive: true, clinicianNote: "Score ≥30 — severe psychological distress; clinical assessment strongly recommended." };
    if (s >= 25) return { severity: "Moderate distress", band: `${s}/50`, positive: true, clinicianNote: "Score 25–29 — moderate distress; consider professional support." };
    if (s >= 20) return { severity: "Mild distress", band: `${s}/50`, positive: false, clinicianNote: "Score 20–24 — mild distress; monitor and consider self-care strategies." };
    return { severity: "Likely well", band: `${s}/50`, positive: false, clinicianNote: "Score <20 — likely to be well." };
  },
};

// ---------------- AUDIT (Alcohol Use Disorders Identification Test, WHO) ----------------
const auditItems: { text: string; opts: { label: string; value: number }[] }[] = [
  { text: "How often do you have a drink containing alcohol?", opts: [
    { label: "Never", value: 0 },
    { label: "Monthly or less", value: 1 },
    { label: "2–4 times a month", value: 2 },
    { label: "2–3 times a week", value: 3 },
    { label: "4 or more times a week", value: 4 },
  ]},
  { text: "How many units of alcohol do you drink on a typical day when you are drinking?", opts: [
    { label: "1–2", value: 0 },
    { label: "3–4", value: 1 },
    { label: "5–6", value: 2 },
    { label: "7–9", value: 3 },
    { label: "10 or more", value: 4 },
  ]},
  { text: "How often have you had 6 or more units on a single occasion in the last year?", opts: [
    { label: "Never", value: 0 },
    { label: "Less than monthly", value: 1 },
    { label: "Monthly", value: 2 },
    { label: "Weekly", value: 3 },
    { label: "Daily or almost daily", value: 4 },
  ]},
  { text: "How often during the last year have you found that you were not able to stop drinking once you had started?", opts: [
    { label: "Never", value: 0 },
    { label: "Less than monthly", value: 1 },
    { label: "Monthly", value: 2 },
    { label: "Weekly", value: 3 },
    { label: "Daily or almost daily", value: 4 },
  ]},
  { text: "How often during the last year have you failed to do what was normally expected from you because of drinking?", opts: [
    { label: "Never", value: 0 },
    { label: "Less than monthly", value: 1 },
    { label: "Monthly", value: 2 },
    { label: "Weekly", value: 3 },
    { label: "Daily or almost daily", value: 4 },
  ]},
  { text: "How often during the last year have you needed a first drink in the morning to get yourself going after a heavy drinking session?", opts: [
    { label: "Never", value: 0 },
    { label: "Less than monthly", value: 1 },
    { label: "Monthly", value: 2 },
    { label: "Weekly", value: 3 },
    { label: "Daily or almost daily", value: 4 },
  ]},
  { text: "How often during the last year have you had a feeling of guilt or remorse after drinking?", opts: [
    { label: "Never", value: 0 },
    { label: "Less than monthly", value: 1 },
    { label: "Monthly", value: 2 },
    { label: "Weekly", value: 3 },
    { label: "Daily or almost daily", value: 4 },
  ]},
  { text: "How often during the last year have you been unable to remember what happened the night before because you had been drinking?", opts: [
    { label: "Never", value: 0 },
    { label: "Less than monthly", value: 1 },
    { label: "Monthly", value: 2 },
    { label: "Weekly", value: 3 },
    { label: "Daily or almost daily", value: 4 },
  ]},
  { text: "Have you or someone else been injured as a result of your drinking?", opts: [
    { label: "No", value: 0 },
    { label: "Yes, but not in the last year", value: 2 },
    { label: "Yes, during the last year", value: 4 },
  ]},
  { text: "Has a relative, friend, doctor or other health worker been concerned about your drinking or suggested you cut down?", opts: [
    { label: "No", value: 0 },
    { label: "Yes, but not in the last year", value: 2 },
    { label: "Yes, during the last year", value: 4 },
  ]},
];
export const audit: ScreeningDef = {
  id: "audit",
  title: "AUDIT",
  fullName: "Alcohol Use Disorders Identification Test (WHO)",
  description: "10-item screen for hazardous and harmful patterns of alcohol use.",
  duration: "~4 min",
  category: "Alcohol Use",
  variant: "full",
  questions: auditItems.map((q, i) => ({ id: `q${i + 1}`, text: q.text, options: q.opts })),
  interpret: (s) => {
    if (s >= 20) return { severity: "Possible dependence", band: `${s}/40`, positive: true, clinicianNote: "Score ≥20 — possible alcohol dependence; specialist assessment recommended." };
    if (s >= 16) return { severity: "Harmful use", band: `${s}/40`, positive: true, clinicianNote: "Score 16–19 — harmful drinking; brief intervention and monitoring advised." };
    if (s >= 8) return { severity: "Hazardous use", band: `${s}/40`, positive: true, clinicianNote: "Score 8–15 — hazardous drinking; brief advice recommended." };
    return { severity: "Low risk", band: `${s}/40`, positive: false, clinicianNote: "Score <8 — low-risk drinking." };
  },
};

// ---------------- AUDIT-C (3-item short alcohol screen) ----------------
export const auditc: ScreeningDef = {
  id: "auditc",
  title: "AUDIT-C",
  fullName: "Alcohol Use Disorders Identification Test — Consumption",
  description: "3-item brief alcohol screen drawn from the AUDIT.",
  duration: "~1 min",
  category: "Alcohol Use",
  variant: "short",
  questions: auditItems.slice(0, 3).map((q, i) => ({ id: `q${i + 1}`, text: q.text, options: q.opts })),
  interpret: (s) => {
    if (s >= 8) return { severity: "Likely alcohol use disorder", band: `${s}/12`, positive: true, clinicianNote: "Score ≥8 — strongly suggests an alcohol use disorder; complete the full AUDIT." };
    if (s >= 4) return { severity: "Positive screen", band: `${s}/12`, positive: true, clinicianNote: "Score ≥4 (men) or ≥3 (women) — positive screen; consider the full AUDIT." };
    return { severity: "Low risk", band: `${s}/12`, positive: false, clinicianNote: "Below AUDIT-C threshold." };
  },
};

// ---------------- CAGE (brief alcohol screen) ----------------
export const cage: ScreeningDef = {
  id: "cage",
  title: "CAGE",
  fullName: "CAGE Alcohol Questionnaire",
  description: "4-item brief screen for problematic drinking. Across your lifetime, have you ever…",
  duration: "~1 min",
  category: "Alcohol Use",
  variant: "short",
  questions: [
    "felt you should Cut down on your drinking?",
    "had people Annoy you by criticising your drinking?",
    "felt bad or Guilty about your drinking?",
    "had a drink first thing in the morning (an Eye-opener) to steady your nerves or get rid of a hangover?",
  ].map((t, i) => ({ id: `q${i + 1}`, text: t, options: yesNo })),
  interpret: (s) => {
    if (s >= 2) return { severity: "Positive screen", band: `${s}/4`, positive: true, clinicianNote: "Score ≥2 — clinically significant; further alcohol-use assessment recommended." };
    if (s === 1) return { severity: "Borderline", band: `${s}/4`, positive: false, clinicianNote: "One positive response; consider follow-up questions or the full AUDIT." };
    return { severity: "Negative", band: `${s}/4`, positive: false, clinicianNote: "Below CAGE threshold." };
  },
};

// ---------------- DAST-10 (Drug Abuse Screening Test) ----------------
// Item 3 is reverse-scored.
const dastItems: { text: string; reverse?: boolean }[] = [
  { text: "Have you used drugs other than those required for medical reasons?" },
  { text: "Do you abuse more than one drug at a time?" },
  { text: "Are you always able to stop using drugs when you want to?", reverse: true },
  { text: "Have you had blackouts or flashbacks as a result of drug use?" },
  { text: "Do you ever feel bad or guilty about your drug use?" },
  { text: "Does your spouse (or parents) ever complain about your involvement with drugs?" },
  { text: "Have you neglected your family because of your use of drugs?" },
  { text: "Have you engaged in illegal activities in order to obtain drugs?" },
  { text: "Have you ever experienced withdrawal symptoms (felt sick) when you stopped taking drugs?" },
  { text: "Have you had medical problems as a result of your drug use (e.g. memory loss, hepatitis, convulsions, bleeding)?" },
];
const dastYesNoRev = [
  { label: "No", value: 1 },
  { label: "Yes", value: 0 },
];
export const dast10: ScreeningDef = {
  id: "dast10",
  title: "DAST-10",
  fullName: "Drug Abuse Screening Test (10-item)",
  description: "10-item screen for problematic non-medical drug use over the past 12 months.",
  duration: "~3 min",
  category: "Drug Use",
  variant: "full",
  questions: dastItems.map((q, i) => ({
    id: `q${i + 1}`,
    text: q.text,
    options: q.reverse ? dastYesNoRev : yesNo,
    reverseScored: q.reverse,
  })),
  interpret: (s) => {
    if (s >= 9) return { severity: "Severe", band: `${s}/10`, positive: true, clinicianNote: "Score 9–10 — severe drug-use problem; intensive assessment and treatment recommended." };
    if (s >= 6) return { severity: "Substantial", band: `${s}/10`, positive: true, clinicianNote: "Score 6–8 — substantial drug-use problem; assessment for treatment recommended." };
    if (s >= 3) return { severity: "Moderate", band: `${s}/10`, positive: true, clinicianNote: "Score 3–5 — moderate drug-use problem; further investigation indicated." };
    if (s >= 1) return { severity: "Low level", band: `${s}/10`, positive: false, clinicianNote: "Score 1–2 — low-level drug-related problems; monitor." };
    return { severity: "No problem", band: `${s}/10`, positive: false, clinicianNote: "No drug-related problems reported." };
  },
};

// ---------------- SCOFF (Eating disorder screen) ----------------
export const scoff: ScreeningDef = {
  id: "scoff",
  title: "SCOFF",
  fullName: "SCOFF Eating Disorder Screening Questionnaire",
  description: "5-item brief screen for possible eating disorders.",
  duration: "~1 min",
  category: "Eating Disorders",
  variant: "short",
  questions: [
    "Do you make yourself Sick because you feel uncomfortably full?",
    "Do you worry you have lost Control over how much you eat?",
    "Have you recently lost more than One stone (about 6.5 kg / 14 lb) in a 3-month period?",
    "Do you believe yourself to be Fat when others say you are too thin?",
    "Would you say that Food dominates your life?",
  ].map((t, i) => ({ id: `q${i + 1}`, text: t, options: yesNo })),
  interpret: (s) => {
    if (s >= 2) return { severity: "Positive screen", band: `${s}/5`, positive: true, clinicianNote: "Score ≥2 — possible eating disorder; full assessment recommended." };
    return { severity: "Below threshold", band: `${s}/5`, positive: false, clinicianNote: "Below SCOFF screening threshold." };
  },
};

// ---------------- WSAS (Work and Social Adjustment Scale) ----------------
const wsasScale = [
  { label: "0 — Not at all", value: 0 },
  { label: "1", value: 1 },
  { label: "2 — Slightly", value: 2 },
  { label: "3", value: 3 },
  { label: "4 — Definitely", value: 4 },
  { label: "5", value: 5 },
  { label: "6 — Markedly", value: 6 },
  { label: "7", value: 7 },
  { label: "8 — Very severely, I can't carry on", value: 8 },
];
export const wsas: ScreeningDef = {
  id: "wsas",
  title: "WSAS",
  fullName: "Work and Social Adjustment Scale",
  description: "5-item measure of how much your mental health is interfering with daily functioning.",
  duration: "~2 min",
  category: "Functional Impairment",
  variant: "full",
  questions: [
    "Because of my problem, my ability to work is impaired.",
    "Because of my problem, my home management (cleaning, tidying, shopping, cooking, looking after home/children, paying bills) is impaired.",
    "Because of my problem, my social leisure activities (with other people, e.g. parties, pubs, outings, entertaining) are impaired.",
    "Because of my problem, my private leisure activities (done alone, e.g. reading, gardening, hobbies, walking) are impaired.",
    "Because of my problem, my ability to form and maintain close relationships with others, including the people I live with, is impaired.",
  ].map((t, i) => ({ id: `q${i + 1}`, text: t, options: wsasScale })),
  interpret: (s) => {
    if (s > 20) return { severity: "Severe impairment", band: `${s}/40`, positive: true, clinicianNote: "Score >20 — severe functional impairment; clinical care strongly recommended." };
    if (s >= 10) return { severity: "Significant impairment", band: `${s}/40`, positive: true, clinicianNote: "Score 10–20 — significant functional impairment; clinical follow-up recommended." };
    return { severity: "Sub-clinical", band: `${s}/40`, positive: false, clinicianNote: "Score <10 — sub-clinical level of functional impairment." };
  },
};

// ---------------- WHO-5 (Wellbeing Index) ----------------
// Higher = better wellbeing. Raw 0–25 (×4 = 0–100). ≤13 raw flags low wellbeing.
const who5Freq = [
  { label: "All of the time", value: 5 },
  { label: "Most of the time", value: 4 },
  { label: "More than half of the time", value: 3 },
  { label: "Less than half of the time", value: 2 },
  { label: "Some of the time", value: 1 },
  { label: "At no time", value: 0 },
];
export const who5: ScreeningDef = {
  id: "who5",
  title: "WHO-5",
  fullName: "WHO-5 Wellbeing Index",
  description: "5-item measure of mental wellbeing. Over the last 2 weeks…",
  duration: "~1 min",
  category: "Wellbeing",
  variant: "full",
  questions: [
    "I have felt cheerful and in good spirits.",
    "I have felt calm and relaxed.",
    "I have felt active and vigorous.",
    "I woke up feeling fresh and rested.",
    "My daily life has been filled with things that interest me.",
  ].map((t, i) => ({ id: `q${i + 1}`, text: t, options: who5Freq })),
  interpret: (s) => {
    const pct = s * 4;
    if (s <= 7) return { severity: "Very low wellbeing", band: `${s}/25 (${pct}%)`, positive: true, clinicianNote: "Very low wellbeing; further depression screening (e.g. PHQ-9) and clinical assessment recommended." };
    if (s <= 12) return { severity: "Low wellbeing", band: `${s}/25 (${pct}%)`, positive: true, clinicianNote: "Score ≤13 raw — low wellbeing; consider further depression screening with PHQ-9." };
    if (s <= 17) return { severity: "Moderate wellbeing", band: `${s}/25 (${pct}%)`, positive: false, clinicianNote: "Moderate wellbeing; monitor and continue self-care." };
    return { severity: "Good wellbeing", band: `${s}/25 (${pct}%)`, positive: false, clinicianNote: "Good wellbeing." };
  },
};

// ---------------- Register new screenings ----------------
SCREENINGS.epds = epds;
SCREENINGS.k10 = k10;
SCREENINGS.audit = audit;
SCREENINGS.auditc = auditc;
SCREENINGS.cage = cage;
SCREENINGS.dast10 = dast10;
SCREENINGS.scoff = scoff;
SCREENINGS.wsas = wsas;
SCREENINGS.who5 = who5;

PRIMARY_SCREENINGS.push(epds, k10, audit, auditc, cage, dast10, scoff, wsas, who5);
